import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import DataListInput from '../../../../basic/DataListInput';
import { WorkGroupClient, WorkGroupModel, EmployeeRefModel } from '../../../../../client/ApiClient';
import { DataCell, DataRow, DataTable } from '../../../../basic/DataTable';
import { RangePaginationState, RangePagination, paginate } from 'components/basic/Pagination';
import { DropdownMenu, DropdownToggle, UncontrolledDropdown, DropdownItem } from 'reactstrap';
import StageActionToolbar from '../generic/StageActionToolbar';
import { stagingPageUpdate, stagingPageCopyMappings, StagingPageCopyMappingsItem } from '../generic/Abstractions';
import { AssignRowStatus, RowStatus } from '../generic';
import { filterColumns, sort } from '../generic/EntityFunctions';

import '../../../../../styles/Icons.scss';
import './WorkGroupStage.scss';

interface WorkGroupStagingPageArgs {
    conversionId: string;
}

type WorkGroupStagingPageProps = RouteComponentProps<WorkGroupStagingPageArgs>;

interface WorkGroupStagingPageState {
    items: (WorkGroupModel & StagingPageCopyMappingsItem)[];
    filter: string;
    destinationCodes: EmployeeRefModel[];
    sort: boolean;
    sortColumn: string;
    mapped: boolean;
    notMapped: boolean;
    dropped: boolean;
    pagination: RangePaginationState;
    buttonLock: boolean;
    filteredColumn: string;
}

export class WorkGroupStagingPage extends React.Component<WorkGroupStagingPageProps, WorkGroupStagingPageState> {
    private client: WorkGroupClient = new WorkGroupClient();

    private readonly copyMappings = stagingPageCopyMappings(this);

    constructor(props: WorkGroupStagingPageProps) {
        super(props);
        this.state = {
            items: [],
            filter: '',
            destinationCodes: [],
            sort: true,
            sortColumn: '',
            mapped: true,
            notMapped: true,
            dropped: true,
            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            buttonLock: true,
            filteredColumn: '',
        };
    }

    componentDidMount() {
        this.getItems();
    }

    setDefaults = (entity: WorkGroupModel) =>
        new WorkGroupModel({
            ...entity,
            destinationCode: entity.destinationCode || '',
            destinationDescription: entity.destinationDescription || '',
        });

    getItems = () => {
        const items = this.client.getAll(this.props.match.params.conversionId, '');
        const refs = this.client.getRef(this.props.match.params.conversionId);
        Promise.all([items, refs])
            .then(result => {
                this.setState({
                    items: result[0]!,
                    destinationCodes: result[1]!,
                }, () => {
                    this.setState({
                        items: sort(this.state.items.map(this.setDefaults), this.state.sortColumn, this.state.sort, []),
                    });
                });
            })
            .catch(() => { });
    }

    DestinationCodes = (): any[] => {
        const arr: any[] = [];
        const dc = [...this.state.destinationCodes].sort((a, b) => (a.sourceCode ?? '').localeCompare(b.sourceCode ?? ''));
        if (dc.length > 0) {
            for (let i = 0; i < dc.length; i++) {
                const name = dc[i].firstName === null ? dc[i].lastName : `${dc[i].firstName} ${dc[i].lastName}`;
                arr.push(<option
                    key={i}
                    data-value={dc[i].sourceCode?.toUpperCase()}
                >
                    {`${dc[i].sourceCode!.trim()} - ${name}`}
                </option>);
            }
        } else {
            arr.push(<option data-value={'readOnly'}> No Employees Available in Target</option>);
        }
        return arr;
    }

    handleRefChange = (item: WorkGroupModel, e: any) => {
        const i = this.state.items.findIndex(v => v.id === item.id);
        const items = this.state.items.map(
            v => v.id === item.id
                ? new WorkGroupModel({
                    ...v,
                    destinationCode: e.value,
                })
                : v
        );
        this.setState({ items });
        if (item.destinationCode?.trim() !== items[i].destinationCode?.trim()) this.put(items[i]);
    }

    handleExcludeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = parseInt(e.target.id, 10);
        const checked = e.target.checked;

        const items = this.state.items;
        const i = items.findIndex(v => v.id === id);
        items[i].exclude = checked;

        this.setState({ items });
        this.put(items[i]);
    }

    put(entity: WorkGroupModel) {
        stagingPageUpdate(this, this.client)
            .withSource('Work Group', entity.sourceDescription!)
            .update(entity)
            .catch(() => { });
    }

    sortItems(column: string) {
        const sorted = this.state.sortColumn === column ? !this.state.sort : true;
        const items = sort(this.state.items, column, sorted, []);
        this.setState({ sort: sorted, sortColumn: column, items });
    }

    mappedCheck(check: string) {
        if (check === 'notMapped') {
            this.setState({ notMapped: !this.state.notMapped });
        } else if (check === 'mapped') {
            this.setState({ mapped: !this.state.mapped });
        } else if (check === 'dropped') {
            this.setState({ dropped: !this.state.dropped });
        }
    }

    lockButton = (lock: boolean) => {
        this.setState({ buttonLock: lock });
    }

    selectFilteredColumn = (filteredColumn: string) => {
        this.setState({ filteredColumn });
    }

    public render() {
        const conversionId = this.props.match.params.conversionId;
        const filteredColumn = this.state.filteredColumn;
        const items = this.state.items
            .map(AssignRowStatus)
            .filter(i =>
                ((filteredColumn === '' || filteredColumn === 'sourceCode') && i.sourceCode && i.sourceCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'sourceDescription') && i.sourceDescription && i.sourceDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationCode') && i.destinationCode && i.destinationCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationDescription') && i.destinationDescription && i.destinationDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())))
            .filter(item =>
                (item.mapped === RowStatus.NotMapped && this.state.notMapped)
                || (item.mapped === RowStatus.Mapped && this.state.mapped)
                || (item.mapped === RowStatus.Dropped && this.state.dropped));

        return (
            <>
                <StageActionToolbar
                    conversionId={conversionId}
                    handleSearch={x => this.setState({ filter: x })}
                    disableMigrate={true}
                    handleCopyMapping={this.copyMappings.submitMappings}
                    sectionId={'workgroups'}
                    mapExcel={true}
                    copyMap={true}
                    getItems={() => this.getItems()}
                    filter={this.state.filter}
                    lockButton={this.lockButton}
                    filterColumns={filterColumns(items)}
                    selectFilteredColumn={this.selectFilteredColumn}
                />
                <DataTable>
                    <thead>
                        <tr className='nonFullWidthRow'>
                            <th onClick={() => this.sortItems('sourceCode')}>Source Code<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sortItems('sourceDescription')}>Source Description<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column' onClick={() => this.sortItems('exclude')}>Exclude<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sortItems('destinationCode')}>Destination Code<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column'>
                                <UncontrolledDropdown setActiveFromChild className="ismappedDropdown">
                                    <DropdownToggle tag="a" className="ismapped" caret>
                                        Mapped
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem className={!this.state.notMapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('notMapped')}>&#x274C; Not Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.mapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('mapped')}>&#x2705; Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.dropped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('dropped')}>&#x2796; Excluded</DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            </th>
                            {/*<th onClick={() => this.sortItems('isUserMapped')} className='checkbox-column'>Auto-Mapped<i className="fa fa-sort"></i></th>*/}
                        </tr>
                    </thead>
                    <tbody>
                        {
                            paginate(items, this.state.pagination, (item, i) => (
                                <DataRow id={item.id.toString()} key={item.id}>
                                    <DataCell>{item.sourceCode}</DataCell>
                                    <DataCell>{item.sourceDescription}</DataCell>
                                    <DataCell className='checkbox-column'>
                                        <input
                                            className='custom-checkbox'
                                            type='checkbox'
                                            id={item.id.toString()}
                                            name='exclude'
                                            checked={item.exclude}
                                            onChange={this.handleExcludeChange}
                                            disabled={this.state.buttonLock}
                                        />
                                    </DataCell>
                                    <DataCell>
                                        <DataListInput
                                            id={`referenceId - ${i}`}
                                            name={item.id.toString()}
                                            value={item.destinationCode ? item.destinationCode.toUpperCase() : undefined}
                                            onValueChange={event => this.handleRefChange(item, event)}
                                            disabled={this.state.buttonLock}
                                        >
                                            <option data-value='' />
                                            {this.DestinationCodes()}
                                        </DataListInput>
                                    </DataCell>
                                    <DataCell className='checkbox-column' itemProp={item.mapped === RowStatus.NotMapped ? 'unmapped' : item.mapped === RowStatus.Mapped ? 'mapped' : 'dropped'}>
                                        {item.mapped === RowStatus.NotMapped
                                            ? <span className='mapped' itemProp='unmapped'>&#x274C;</span>
                                            : item.mapped === RowStatus.Mapped
                                                ? <span className='mapped' itemProp='mapped'>&#x2705;</span>
                                                : <span className='mapped' itemProp='dropped'>&#x2796;</span>}
                                    </DataCell>
                                    {/*<DataCell*/}
                                    {/*    className='checkbox-column'*/}
                                    {/*    itemProp={!item.isUserMapped && item.isMapped ? 'auto-mapped' : ''}*/}
                                    {/*>*/}
                                    {/*    {!item.isUserMapped && item.isMapped ? < span className='mapped' itemProp='auto-mapped'>&#x2705;</span> : ''}*/}
                                    {/*</DataCell>*/}
                                </DataRow>
                            ))
                        }
                    </tbody>
                </DataTable>
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={items.length}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
            </>
        );
    }
}
